import { isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../../../providers/userContextProvider';
import { useQueryBuilder } from './utils';
import {
  Typography, Box, Grid, Skeleton,
} from '../../../../1-primative';
import { Card, CardContent, Selector } from '../../../../2-component';
import { PieChart } from '../../../../2-component/pieChart/pieChart';
import { formatPercentValue, formatCompactNumber } from '../../../../../util';
import { useThemeTokens } from '../../../../../providers/themeTokenProvider';
import { TranslatedString } from '../../../../../interfaces/shared';
import { translateBackend } from '../../../../../assets/i18n/config';

const accountTypeQuery = (activeOrganization: any) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'state',
      comparison: 'EQUALS',
      value: 'ACTIVE',
    },
  ],
  type: 'SUB_ACCOUNT',
  grouping: {
    valueField: 'marketValueCents',
    field: 'accountType',
    valueAggregation: 'SUM',
    type: 'GROUPING',
  },
});

const modelPortfolioQuery = (activeOrganization: any) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'state',
      comparison: 'EQUALS',
      value: 'ACTIVE',
    },
  ],
  type: 'SUB_ACCOUNT',
  grouping: {
    field: 'financialProductName',
    interval: null,
    type: 'GROUPING',
    valueAggregation: 'SUM',
    valueField: 'marketValueCents',
  },
});

const childOrganizationQuery = (activeOrganization: any) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'state',
      comparison: 'EQUALS',
      value: 'ACTIVE',
    },
  ],
  type: 'SUB_ACCOUNT',
  grouping: {
    field: 'organizationName',
    interval: null,
    type: 'GROUPING',
    valueAggregation: 'SUM',
    valueField: 'marketValueCents',
  },
});

export const AumExplorer = ({ options }: { options: {
  customTitle: TranslatedString,
  display: 'pie' | 'bar',
  accountType: boolean,
  modelPortfolio: boolean,
  childOrganization: boolean,
} }) => {
  const {
    customTitle, accountType, modelPortfolio, childOrganization,
  } = options;
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['homePageConfiguration', 'accountTypes']);
  const [groupBy, setGroupBy] = useState<string>(accountType ? 'accountType' : modelPortfolio ? 'modelPortfolio' : childOrganization ? 'childOrganization' : 'accountType');
  const [activeItem, setActiveItem] = useState<any>(null);
  const [totalValue, setTotalValue] = useState<number>(0);
  const [sData, setSData] = useState<any[]>([]);
  const [query, setQuery] = useState<any>(
    accountType
      ? accountTypeQuery(activeOrganization)
      : modelPortfolio
        ? modelPortfolioQuery(activeOrganization)
        : childOrganization
          ? childOrganizationQuery(activeOrganization)
          : accountTypeQuery(activeOrganization),
  );
  const { data, loading } = useQueryBuilder(query);

  const sortedData = () => {
    if (loading) return;
    const d = data.generateReport?.grouping.filter((x: any) => x.value > 0).sort((a: any, b: any) => b.value - a.value).map((x: any) => ({
      ...x,
      key: groupBy === 'accountType' ? t(`accountTypes:${x.key}`) : x.key,
    })) ?? [];
    if (d.length > 8) {
      const topEight = d.slice(0, 6);
      const otherItems = d.slice(6);
      const otherValue = otherItems.reduce((acc: number, curr: any) => acc + curr.value, 0);

      setSData([...topEight, {
        key: t('other'),
        value: otherValue,
      }]);
      return;
    }
    setSData(d);
  };

  const { ref, sys } = useThemeTokens();

  const colors = [
    ref.palette.supportOne50,
    ref.palette.supportTwo50,
    ref.palette.supportThree50,
    ref.palette.supportFour50,
    ref.palette.supportFive50,
    ref.palette.supportSix50,
  ];

  useEffect(() => {
    if (data?.generateReport?.grouping) {
      setTotalValue(data?.generateReport.grouping.reduce((acc: number, curr: any) => acc + curr.value, 0));
      sortedData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, activeOrganization]);

  useEffect(() => {
    if (groupBy === 'accountType') {
      setQuery(accountTypeQuery(activeOrganization));
    } else if (groupBy === 'modelPortfolio') {
      setQuery(modelPortfolioQuery(activeOrganization));
    } else if (groupBy === 'childOrganization') {
      setQuery(childOrganizationQuery(activeOrganization));
    }
  }, [groupBy, activeOrganization]);

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent sx={{ paddingBottom: '16px! important' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
          <Typography variant='titleLarge'>{translateBackend(customTitle)}</Typography>
          <Box>
          <Selector
              onChange={(e: any) => setGroupBy(e.target.value as 'accountType' | 'modelPortfolio' | 'childOrganization')}
              selectedOptionType={groupBy}
              options={[
                ...(accountType
                  ? [{ type: 'accountType', label: t('aumExplorer.accountType'), dataTestId: 'holdings-pie-chart-selection-account-type' }]
                  : []),
                ...(modelPortfolio
                  ? [{ type: 'modelPortfolio', label: t('aumExplorer.modelPortfolio'), dataTestId: 'holdings-pie-chart-selection-model-portfolio' }]
                  : []),
                // TODO: organizationName grouping is failing right now
                // ...(childOrganization
                //   ? [{ type: 'childOrganization', label: t('aumExplorer.childOrganization'), dataTestId: 'holdings-pie-chart-selection-child-organization' }] : []),
              ]}
            />
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box display='flex' position='relative' alignItems='center' justifyContent='center' pl={{ xs: 3, md: 10 }} pr={{ xs: 3, md: 10 }} pt={3} pb={3}>
              { loading ? (
                <Skeleton variant='circular' height='400px' width='400px' />
              ) : (
                <PieChart
                  datasets={[{
                    label: '',
                    data: sData.map((x: any) => x.value),
                  }]}
                  labels={sData.map((x: any) => t(x.key))}
                  onHover={(index: number | undefined) => setActiveItem(!isUndefined(index) ? sData[index] : null)}
                  customColors={sData.map((x: any, i: number) => colors[i % 6])}
                />
              )}
              { !loading && (
                <Box textAlign='center' position='absolute' left='50%' top='50%' sx={{ transform: 'translate(-50%,-50%)' }} >
                  {
                    activeItem ? (
                      <>
                        <Typography data-testid={'active-pie-chart-holdings-asset-title'} sx={{ color: sys.color.onSurfaceVariant }} variant='bodyLarge'>
                          {activeItem.key}
                        </Typography>
                        <Typography
                          data-testid='active-pie-chart-holdings-value'
                          variant='headingLarge'
                        >
                          {formatCompactNumber((activeItem?.value ?? 0) / 100, { isCurrency: true })}
                        </Typography>
                        <Typography
                          data-testid='active-pie-chart-holdings-percentage'
                          weight='bold'
                          variant='bodyLarge'
                        >
                          {(activeItem?.value ?? 0) >= 0 && `${formatPercentValue((activeItem?.value || 0) / totalValue)} ${t('ofTotal')}`}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography data-testid='pie-chart-holdings-asset-title' sx={{ color: sys.color.onSurfaceVariant }} variant='bodyLarge'>
                          {groupBy === 'accountType' ? t('aumExplorer.accountType') : groupBy === 'modelPortfolio' ? t('aumExplorer.modelPortfolio') : t('aumExplorer.childOrganization')}
                        </Typography>
                        <Typography
                          data-testid='pie-chart-holdings-value'
                          variant='headingLarge'
                        >
                          {formatCompactNumber(totalValue / 100, { isCurrency: true })}
                        </Typography>
                        <Typography data-testid='pie-chart-holdings-percentage' weight='bold' variant='bodyLarge'>100% {t('ofTotal')}</Typography>
                      </>
                    )
                  }
                </Box>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display='flex' justifyContent='center' flexDirection='column' minHeight='100%'>
              {loading && (
                <Box display='flex' justifyContent='center' alignItems='center' height='100%' flexDirection='column'>
                  <Skeleton variant='rectangular' height='50px' width='100%' sx={{ mb: '2px' }} />
                  <Skeleton variant='rectangular' height='50px' width='100%' sx={{ mb: '2px' }} />
                  <Skeleton variant='rectangular' height='50px' width='100%' sx={{ mb: '2px' }} />
                  <Skeleton variant='rectangular' height='50px' width='100%' sx={{ mb: '2px' }} />
                  <Skeleton variant='rectangular' height='50px' width='100%' sx={{ mb: '2px' }} />
                </Box>
              )}
              {!loading && sData.map((x: any, i: number) => (
                <Box
                  data-testid={`pie-chart-holdings-tile-${i}`}
                  key={x.translatedName?.en ? `${x.translatedName.en}-${i}` : i}
                  onMouseEnter={() => setActiveItem(x)}
                  onMouseLeave={() => setActiveItem(undefined)}
                  display='flex'
                  justifyContent='space-between'
                  sx={{
                    borderBottom: activeItem?.translatedName !== x.translatedName && i + 1 < data.length ? `2px solid ${sys.color.surfaceContainer}` : `2px solid ${sys.color.surface}`,
                    p: 1.5,
                    backgroundColor: activeItem?.translatedName === x.translatedName ? sys.color.surfaceContainer : 'none',
                    borderRadius: activeItem?.translatedName === x.translatedName ? sys.borderRadius.md : '0px',
                  }}
                >
                  <Box display='flex' flexDirection='row' justifyContent='start' alignItems='center'>
                    <Box sx={{
                      borderRadius: '10px', height: '8px', width: '8px', backgroundColor: colors[i % 6], mr: 1,
                    }} />
                    <Typography variant='bodyLarge'>{x.key}</Typography>
                  </Box>
                  <Typography variant='bodyLarge' weight='bold' sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatPercentValue(x.value / totalValue)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
